import React from 'react';

import { cn } from '@/lib/utils';

interface ErrorBoundaryProps {
  children: React.ReactNode;
  /** Fallback UI to show on error. Receives the error and a reset callback, or a static ReactNode. */
  fallback?: React.ReactNode | ((error: Error, reset: () => void) => React.ReactNode);
  /** Optional className for the default fallback container. */
  className?: string;
}

interface ErrorBoundaryState {
  error: Error | null;
}

/**
 * React Error Boundary — catches render errors in children
 * and displays a fallback UI instead of crashing the entire tree.
 *
 * Usage:
 *   <ErrorBoundary>
 *     <VisualizerRenderer ... />
 *   </ErrorBoundary>
 *
 *   <ErrorBoundary fallback={(err, reset) => <MyCustomFallback />}>
 *     <SomeWidget />
 *   </ErrorBoundary>
 */
export class ErrorBoundary extends React.Component<ErrorBoundaryProps, ErrorBoundaryState> {
  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = { error: null };
  }

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return { error };
  }

  componentDidCatch(error: Error, info: React.ErrorInfo) {
    console.error('[ErrorBoundary] Caught render error:', error, info.componentStack);
  }

  private handleReset = () => {
    this.setState({ error: null });
  };

  render() {
    if (this.state.error) {
      if (this.props.fallback) {
        return typeof this.props.fallback === 'function'
          ? this.props.fallback(this.state.error, this.handleReset)
          : this.props.fallback;
      }

      return (
        <div className={cn('flex flex-col items-center justify-center gap-3 p-6 text-center', this.props.className)}>
          <div className="text-sm font-medium text-destructive">Something went wrong</div>
          <p className="text-xs text-muted-foreground max-w-sm">
            {this.state.error.message}
          </p>
          <button
            onClick={this.handleReset}
            className="text-xs text-primary underline-offset-4 hover:underline"
          >
            Try again
          </button>
        </div>
      );
    }

    return this.props.children;
  }
}
